import React, { useCallback, useEffect, useMemo, useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import {
  Text,
  Flex,
  Tabs,
  Tab,
  Card,
  Link,
  Table,
  TableCell,
  TableRow,
  TableBody,
} from "@jsq/ds/core";
import { PaymentServicePaymentBatchStatus } from "../../graphql/supergraph.index";
import { toDbId } from "../../utils/graphql_utils";
import Money from "../Money";
import { batchStatusColor } from "../PaymentBatchesTable/styles_vanilla.css";
import { BatchStatusUIConfig, PaymentRowItem } from "../PaymentBatchesTable/types";
import {
  BatchStatusForUI,
  batchStatusUIConfig,
} from "../PaymentBatchesTable/util/batchStatusUtil";
import { PositionsTable } from "../PositionsTable";
import { SequenceTextWithIcon } from "../SequenceTextWithIcon";
import PaymentApproveButton from "./PaymentApproveButton";
import PaymentApproveButtonTooltip from "./PaymentApproveButtonTooltip";
import PaymentDenyButton from "./PaymentDenyButton";
import {
  paymentDetails,
  commonTabs,
  paymentOriginationDetailsCard,
  paymentDetailsCard,
  cardTitle,
  upperContainer,
  middleContainer,
  lowerContainer,
  statusRow,
  tableCell,
  tableRow,
  batchStatusText,
  bottomButtonBar,
} from "./styles_vanilla.css";

/* enum value to switch between different tabs */
enum PaymentDetailsTabs {
  Details = "Payment details",
  Positions = "Positions",
}

export type PaymentDetailsProps = {
  paymentRowItem: PaymentRowItem;
  userId: number;
  onBatchStatusChange: (batchStatus: BatchStatusUIConfig) => void;
};

export const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  paymentRowItem,
  userId,
  onBatchStatusChange,
}: PaymentDetailsProps) => {
  const [currentTab, setCurrentTab] = useState<PaymentDetailsTabs>(PaymentDetailsTabs.Details);
  const handleTabChange = (currentTab: PaymentDetailsTabs) => {
    setCurrentTab(currentTab);
  };

  const entityUrl = useMemo<string>(
    () => `https://${paymentRowItem.hostName}/account/${toDbId(paymentRowItem.entityId)}`,
    [paymentRowItem],
  );

  const distributionUrl = useMemo<string | null>(
    () =>
      paymentRowItem.batchId
        ? `https://${paymentRowItem.hostName}/distribution_batch/${paymentRowItem.batchId}`
        : null,
    [paymentRowItem],
  );

  const isUserSameAsCreator = useMemo<boolean>(() => {
    return userId === toDbId(paymentRowItem.creatorID);
  }, [userId, paymentRowItem.creatorID]);

  const [modifiedBatchStatus, setModifiedBatchStatus] =
    useState<PaymentServicePaymentBatchStatus>();
  const updateBatchStatus = useCallback((status: PaymentServicePaymentBatchStatus) => {
    setModifiedBatchStatus(status);
  }, []);

  const batchStatus: BatchStatusUIConfig = useMemo(() => {
    if (modifiedBatchStatus) {
      return batchStatusUIConfig(modifiedBatchStatus);
    } else {
      return paymentRowItem.batchStatusConfig;
    }
  }, [modifiedBatchStatus, paymentRowItem.batchStatusConfig]);

  const isBatchStatusReadyForReview = useMemo<boolean>(() => {
    return batchStatus.label === BatchStatusForUI.ReadyForReview.valueOf();
  }, [batchStatus.label]);

  const isBatchStatusDenied = useMemo<boolean>(() => {
    return batchStatus.label === BatchStatusForUI.Denied.valueOf();
  }, [batchStatus.label]);

  const renderApproveButtonFn = useCallback(() => {
    if (isBatchStatusDenied) {
      return (
        <PaymentApproveButton
          variant="muted"
          disabled={true}
          paymentBatchId={paymentRowItem.id}
          approvedCallback={updateBatchStatus}
        />
      );
    } else if (isUserSameAsCreator) {
      return (
        <PaymentApproveButtonTooltip
          id={paymentRowItem.id}
          tooltipContent="You cannot approve this payment batch because you created it."
        />
      );
    } else {
      return (
        <PaymentApproveButton
          variant="primary"
          disabled={!isBatchStatusReadyForReview}
          paymentBatchId={paymentRowItem.id}
          approvedCallback={updateBatchStatus}
        />
      );
    }
  }, [
    isBatchStatusDenied,
    isUserSameAsCreator,
    isBatchStatusReadyForReview,
    paymentRowItem.id,
    updateBatchStatus,
  ]);

  useEffect(() => {
    onBatchStatusChange(batchStatus);
  }, [batchStatus, onBatchStatusChange]);

  return (
    <Flex className={paymentDetails} flexDirection="column" data-testid="paymentDetailsView">
      <Flex className={upperContainer}>
        <Table className={statusRow}>
          {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
          {/* @ts-ignore */}
          <TableBody>
            <TableRow>
              <TableCell className={tableCell}>
                <Text
                  className={batchStatusText}
                  style={assignInlineVars({
                    [batchStatusColor]: batchStatus.labelColor,
                  })}
                >
                  {batchStatus.label}
                </Text>
                <Text variant="secondary-md">Batch status</Text>
              </TableCell>
              <TableCell>
                <SequenceTextWithIcon items={paymentRowItem.paymentStatus} />
                <Text variant="secondary-md">Payment status</Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Flex>
          <Tabs className={commonTabs}>
            <Tab
              role="tab"
              text={PaymentDetailsTabs.Details}
              onClick={() => handleTabChange(PaymentDetailsTabs.Details)}
              active={currentTab === PaymentDetailsTabs.Details}
            ></Tab>
            <Tab
              role="tab"
              text={PaymentDetailsTabs.Positions}
              onClick={() => handleTabChange(PaymentDetailsTabs.Positions)}
              active={currentTab === PaymentDetailsTabs.Positions}
            ></Tab>
          </Tabs>
        </Flex>
      </Flex>

      <Flex className={middleContainer}>
        {currentTab === PaymentDetailsTabs.Details && (
          <Flex gap="medium" flexDirection="column">
            <Card className={paymentOriginationDetailsCard}>
              <Flex flexDirection="column">
                <Text className={cardTitle} variant="secondary-lg">
                  PAYMENT ORIGINATION DETAILS
                </Text>
                <Table>
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                  {/* @ts-ignore */}
                  <TableBody>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Client</Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="regular-md">{paymentRowItem.clientName}</Text>
                      </TableCell>
                    </TableRow>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Investment entity</Text>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={entityUrl}
                          variant="muted"
                          align="left"
                          icon="external-link"
                          iconPosition="right"
                        >
                          {paymentRowItem.entityName}
                        </Link>
                      </TableCell>
                    </TableRow>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Originating account</Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="regular-md">{paymentRowItem.originatingAccount}</Text>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Flex>
            </Card>
            <Card className={paymentDetailsCard}>
              <Flex flexDirection="column">
                <Text className={cardTitle} variant="secondary-lg">
                  PAYMENT DETAILS
                </Text>
                <Table>
                  {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                  {/* @ts-ignore */}
                  <TableBody>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Distribution</Text>
                      </TableCell>
                      <TableCell>
                        {distributionUrl && (
                          <Link
                            href={distributionUrl}
                            variant="muted"
                            align="left"
                            icon="external-link"
                            iconPosition="right"
                          >
                            {paymentRowItem.distributionName || ""}
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Batch created date</Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="regular-md">{paymentRowItem.createdDate}</Text>
                      </TableCell>
                    </TableRow>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Payment amount</Text>
                      </TableCell>
                      <TableCell>
                        <Money money={paymentRowItem.totalAmount} />
                      </TableCell>
                    </TableRow>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Positions</Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="regular-md">{paymentRowItem.receivingAccount}</Text>
                      </TableCell>
                    </TableRow>
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Payment description for recipients</Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="regular-md">
                          {paymentRowItem.paymentDescription || "None"}
                        </Text>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Flex>
            </Card>
          </Flex>
        )}
        {currentTab === PaymentDetailsTabs.Positions && (
          <Flex gap="medium">
            <PositionsTable
              id={paymentRowItem.id}
              payments={paymentRowItem.payments}
              batchId={paymentRowItem.batchId}
              hostName={paymentRowItem.hostName}
              batchLabel={batchStatus.label}
            />
          </Flex>
        )}
      </Flex>

      <Flex className={lowerContainer}>
        <Flex className={bottomButtonBar}>
          <PaymentDenyButton
            paymentBatchId={paymentRowItem.id}
            disabled={!isBatchStatusReadyForReview || isUserSameAsCreator}
            deniedCallback={updateBatchStatus}
          />
          {renderApproveButtonFn()}
        </Flex>
      </Flex>
    </Flex>
  );
};
